// --- Core Dependencies
import React, {useEffect} from 'react';
import {useSelector} from 'react-redux';
// --- UI Components
import Typography from '@dtci/spellbook/Typography';
import {Col, Row} from 'antd';
import Button from '@dtci/spellbook/Button';
import {Pagination} from '../../components/Pagination';
import {SharedSectionsGrid} from './components/grid';
import {SharedSectionsFilterForm} from './components/filter-form';
import TwoRowsLayout from './layout/two-row';
// --- Hooks
import {useSharedSectionsActions} from '../../store/sharedSections/useSharedSections';
import {useUsersActions} from '../../store/users/useUsers';
import {useLoading} from '../../utils/hooks/useLoading';
// --- Utilities
import {getItemRange} from '../../utils/pagination';
import {filterCriteria} from '../../utils/filterCriteria';
import {exportToCsv} from '../../utils/exportToCsv';
// --- API
import {datastageApi} from '../../api';
import ToastNotificationProvider from "../../components/ToastNotifications";
import {tracker} from '../../analytics/snowplow';
import { getCurrentUser } from '../../utils/localStorageService';
/**
 * SharedSectionsPage Component
 * Renders the Shared Sections page with filters, grid, and pagination.
 *
 * @returns {JSX.Element} The Shared Sections Page component.
 */
export const SharedSectionsPage = () => {
    React.useEffect(() => {
        var currentUser = getCurrentUser();        
        tracker.setUserId(currentUser);
        tracker.trackPageView({ title: 'Shared Sections' });
    }, []);
    const {setLoadingTrue, setLoadingFalse} = useLoading();
    const {updateSharedSections} = useSharedSectionsActions();
    const {updateUsers} = useUsersActions();
    const {totalPages, currentPage, filterForm, sectionSort, sectionRange, itemsPerPage, totalItems} =
        useSelector((s) => s.sharedSections);

    // Fetches the list of users when the component mounts
    useEffect(() => {
        fetchUsers();
    }, []);

    // Fetches the list of users and updates the state
    const fetchUsers = async () => {
        try {
            const users = await datastageApi.users();
            updateUsers({usersList: users});
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    // Fetches shared sections based on the current page and range
    const fetchSharedSections = async (pageNumber, range) => {
        try {
            setLoadingTrue();
            const data = await datastageApi.sharedSections(
                filterCriteria(filterForm, '', 'sharedSections'),
                range,
                sectionSort,
            );
            updateSharedSections({
                shared: data.content,
                totalItems: data.totalElements,
                totalPages: data.totalPages,
                currentPage: data.pageable?.pageNumber,
                sectionRange: range,
            });
        } catch (error) {
            console.error('Error fetching shared sections:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Handles page change in the pagination component
    const handlePageChange = (pageNumber) => {
        if (pageNumber !== currentPage) {
            const nextPageRange = getItemRange(pageNumber, itemsPerPage, totalItems);
            fetchSharedSections(pageNumber, nextPageRange);
        }
    };

    // Exports the shared sections data to a CSV file
    const handleExport = async () => {
        try {
            setLoadingTrue();
            const data = await datastageApi.sharedSections(
                filterCriteria(filterForm, '', 'sharedSections'),
                '0,999',
                sectionSort,
            );
            exportToCsv(data.content, 'clearanceMemo_sharedSections');
        } catch (error) {
            console.error('Error exporting shared sections:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Refreshes the shared sections data
    const handleRefresh = () => {
        fetchSharedSections(currentPage, productRange);
    };

    // Opens the modal for creating a new shared section
    const openCreateModal = async () => {
        try {
            setLoadingTrue();
            const data = await datastageApi.tags({}, [0, 24], ['id', 'DESC']);
            const tagOptions = data?.content?.map(({tagName, id}) => ({
                label: tagName,
                value: id,
            }));
            updateSharedSections({
                isSSModalOpen: true,
                modalMode: 'create',
                tagOptions,
                section: {},
                clearanceMemoTags: [],
            });
        } catch (error) {
            console.error('Error opening shared section modal:', error);
        } finally {
            setLoadingFalse();
        }
    };

    return (
        <>
            <Row style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20}}>

                <Col>
                    <Typography variant="T50" style={{marginLeft: 20}}>
                        Shared Sections
                    </Typography>
                </Col>
                <Col style={{display: 'flex', gap: 8}}>
                    <Button
                        iconType="fa-solid fa-arrow-down-to-line"
                        labelText="Export"
                        styleType="link"
                        onClick={handleExport}
                    >
                        Export
                    </Button>
                    <Button
                        iconType="fa-solid fa-plus"
                        labelText="Create"
                        styleType="link"
                        onClick={openCreateModal}
                    >
                        Create
                    </Button>
                    <Button
                        iconType="fa-solid fa-rotate"
                        labelText="Refresh"
                        styleType="link"
                        onClick={handleRefresh}
                    >
                        Refresh
                    </Button>
                </Col>
            </Row>

            {/* Layout with filter form and grid */}
            <TwoRowsLayout>
                <main data-position="top">
                    <SharedSectionsFilterForm/>
                </main>
                <main data-position="bottom">
                    <SharedSectionsGrid/>
                </main>
            </TwoRowsLayout>

            {/* Pagination component */}
            <Pagination
                pageCount={totalPages}
                onPageChange={(e) => handlePageChange(e.selected)}
                previousLabel="←"
                nextLabel="→"
                forcePage={currentPage}
            />
            <ToastNotificationProvider/>
        </>
    );
};
